import React, { FC } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  useTheme,
  Button,
} from '@mui/material'
import Grid from '@mui/material/Grid'
import BarChartIcon from '@mui/icons-material/BarChart'
import DashboardIcon from '@mui/icons-material/Dashboard'
import SettingsIcon from '@mui/icons-material/Settings'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import DownloadIcon from '@mui/icons-material/Download'
import { useNavigate } from 'react-router-dom'
import { GUIDE_PDF_URL } from '../../../../constants/const'
import { BackButton } from '../../components/molecules/BackButton'
import { LogoutButton } from '../../../../components/organisms/LogoutButton'
import { colorTokens } from '../../../../shared/styles/colorTokens'
import { FONT_WEIGHT } from '../../../../shared/styles/typography'
import { RADIUS } from '../../../../shared/styles/radii'
import { currentGuidePath } from '../../../../constants/guidePaths'

// 28 spec の quiet button: 平常時はグレー文字のみ、hoverでうっすら塗り（罫線レス）
const quietButtonSx = {
  color: colorTokens.fg.muted,
  fontWeight: FONT_WEIGHT.medium,
  px: 1.5,
  py: 0.5,
  minWidth: 0,
  borderRadius: `${RADIUS.md}px`,
  '& .MuiButton-startIcon': { mr: 0.75 },
  '&:hover': {
    backgroundColor: colorTokens.overlay.hover,
    color: colorTokens.fg.default,
  },
} as const

export const PortalHomePage: FC = () => {
  const navigate = useNavigate()
  const theme = useTheme()

  const portalItems = [
    {
      title: '利用状況確認',
      description: 'OCR処理件数や利用統計を確認できます',
      icon: (
        <BarChartIcon sx={{ fontSize: 48, color: theme.palette.grey[500] }} />
      ),
      path: '/portal/request-event-statistics',
    },
    // Melphinでは採用薬の一時変更登録は非表示
    // {
    //   title: '採用薬の一時変更登録',
    //   description: '薬局での採用薬変更の登録と管理を行えます',
    //   icon: (
    //     <MedicationIcon sx={{ fontSize: 48, color: theme.palette.grey[500] }} />
    //   ),
    //   path: '/portal/med_change',
    // },
    {
      title: 'OCR処理設定',
      description: '処理制御設定を管理できます',
      icon: (
        <SettingsIcon sx={{ fontSize: 48, color: theme.palette.grey[500] }} />
      ),
      path: '/portal/ocr-settings',
    },
    // Melphinでは表示処理設定は非表示
    // {
    //   title: '表示処理設定',
    //   description: '画面表示やUI、通知に関する設定を管理できます',
    //   icon: (
    //     <DisplaySettingsIcon
    //       sx={{ fontSize: 48, color: theme.palette.grey[500] }}
    //     />
    //   ),
    //   path: '/portal/display-settings',
    // },
  ]

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: theme.palette.grey[50],
        py: 4,
        px: { xs: 2, sm: 4 },
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 'bold',
              color: theme.palette.primary.dark,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <DashboardIcon sx={{ fontSize: 32 }} />
            管理ポータル
          </Typography>
          {/* ヘッダアクションは 28 spec の quiet button（枠線・塗りなし、hoverで overlay.hover） */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button
              startIcon={<MenuBookIcon sx={{ fontSize: 18 }} />}
              onClick={() => window.open(currentGuidePath(), '_blank')}
              sx={quietButtonSx}
            >
              困ったときガイド
            </Button>
            {/* ログイン済みなので所属法人版PDF（あれば）が落ちる */}
            <Button
              startIcon={<DownloadIcon sx={{ fontSize: 18 }} />}
              href={GUIDE_PDF_URL}
              target="_blank"
              rel="noopener"
              sx={quietButtonSx}
            >
              印刷用PDF
            </Button>
          </Box>
        </Box>

        <Typography
          variant="body1"
          sx={{
            mb: 4,
            color: theme.palette.text.secondary,
            textAlign: 'center',
          }}
        ></Typography>

        <Grid container spacing={3} sx={{ mt: 2 }}>
          {portalItems.map((item, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  },
                }}
              >
                <CardActionArea
                  onClick={() => navigate(item.path)}
                  sx={{
                    height: '100%',
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                  }}
                >
                  <CardContent sx={{ flex: 1, width: '100%' }}>
                    <Box sx={{ mb: 2 }}>{item.icon}</Box>
                    <Typography
                      variant="h6"
                      component="h2"
                      sx={{
                        mb: 2,
                        fontWeight: 'bold',
                        color: theme.palette.text.primary,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        lineHeight: 1.6,
                      }}
                    >
                      {item.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <BackButton text="処方箋画面へ戻る" onClick={() => navigate('/')} />

      {/* ログアウトボタン */}
      <LogoutButton />
    </Box>
  )
}
